import { Panel } from '@src/components/Panel/Panel';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import { useState, type ReactNode } from 'react';
import { PremiumFeatureBadge } from '@src/components/PremiumBadge/PremiumFeatureBadge';
import { OrganizerPanelHeading } from './OrganizerPanelHeading';
import './OrganizerSection.css';

export function OrganizerPanelHeader({ children }: { children: ReactNode }) {
  return <div className="organizer-panel-section__header">{children}</div>;
}

/** Tab content stays visible unless a panel explicitly opts into a disclosure. */
export function OrganizerPanelSection({ embedded = false, title, premium = false,
  premiumFeature, icon, id, children, hideHeading = false, headerActions, collapsible = false, defaultOpen = false, storageKey,
}: {
  mobile: boolean;
  storageKey?: string;
  collapsible?: boolean;
  hideHeading?: boolean;
  headerActions?: ReactNode;
  embedded?: boolean;
  gameKey: string;
  title: ReactNode;
  premium?: boolean;
  premiumFeature?: string;
  icon: ReactNode;
  id: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [expanded, setExpanded] = useState(() => {
    try { const saved = storageKey ? localStorage.getItem(storageKey) : null; return saved === null ? defaultOpen : saved === 'open'; }
    catch { return defaultOpen; }
  });
  if (embedded) return <div data-testid={`organizer-section-${id}`}>{children}</div>;
  if (collapsible) return <Panel as="details" open={expanded} onToggle={event => {
    const next = (event.currentTarget as HTMLDetailsElement).open;
    setExpanded(next);
    try { if (storageKey) localStorage.setItem(storageKey, next ? 'open' : 'closed'); } catch { /* Storage may be unavailable. */ }
  }} padding="none" elevated id={id} className="organizer-section organizer-panel-section" testId={`organizer-section-${id}`}>
    <summary>
      <span className="organizer-section__icon">{icon}</span>
      <h2 className="organizer-section__title" style={{ margin: 0, font: 'inherit', fontWeight: 700 }}>{title}</h2>
      {headerActions ?? (premium && premiumFeature ? <PremiumFeatureBadge feature={premiumFeature} /> : null)}
      <ChevronDownIcon className="organizer-section__chevron" />
    </summary>
    <div className="organizer-section__body">{children}</div>
  </Panel>;
  return <Panel as="section" padding="none" elevated id={id} className="organizer-section organizer-panel-section" testId={`organizer-section-${id}`}>
    {!hideHeading && <OrganizerPanelHeader>
      <OrganizerPanelHeading icon={icon}
        actions={headerActions ?? (premium && premiumFeature ? <PremiumFeatureBadge feature={premiumFeature} /> : undefined)}>
        {title}
      </OrganizerPanelHeading>
    </OrganizerPanelHeader>}
    <div className={`organizer-section__body${hideHeading ? ' organizer-panel-section__body--no-heading' : ''}`}>{children}</div>
  </Panel>;
}
